import { cn } from '@/lib/utils'
import { colors, accent } from '@/lib/tokens'

type BadgeColor = {
  bg: string
  border: string
  color: string
}

const METHOD_COLORS: Record<string, BadgeColor> = {
  GET:    { bg: 'rgba(56, 189, 248, 0.08)',  border: 'rgba(56, 189, 248, 0.22)',  color: '#38bdf8' },
  POST:   { bg: colors.accentSubtle,          border: colors.accentBorder,          color: accent },
  PATCH:  { bg: 'rgba(167, 139, 250, 0.08)', border: 'rgba(167, 139, 250, 0.22)', color: '#a78bfa' },
  PUT:    { bg: 'rgba(167, 139, 250, 0.08)', border: 'rgba(167, 139, 250, 0.22)', color: '#a78bfa' },
  DELETE: { bg: colors.errorSubtle,            border: colors.errorBorder,           color: colors.error },
}
const DEFAULT_METHOD_COLOR: BadgeColor = { bg: colors.bgHover, border: colors.borderDefault, color: colors.textSecondary }

/**
 * Compact mono-font badge for labels, methods, statuses, etc.
 *
 * Quick helpers:
 * - `Badge.method("GET")` — HTTP method with canonical color
 * - `<Badge color={...}>text</Badge>` — custom color object
 */
function Badge({
  children,
  color,
  className,
  style,
}: {
  children: React.ReactNode
  color?: BadgeColor
  className?: string
  style?: React.CSSProperties
}) {
  const c = color ?? DEFAULT_METHOD_COLOR
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-[4px] px-2 py-0.5 text-[11px] font-bold leading-tight',
        className,
      )}
      style={{
        fontFamily: "var(--font-mono)",
        letterSpacing: '0.05em',
        backgroundColor: c.bg,
        border: `1px solid ${c.border}`,
        color: c.color,
        ...style,
      }}
    >
      {children}
    </span>
  )
}

Badge.method = function MethodBadge({
  method,
  className,
}: {
  method: string
  className?: string
}) {
  return (
    <Badge color={METHOD_COLORS[method] ?? DEFAULT_METHOD_COLOR} className={className}>
      {method}
    </Badge>
  )
}

export { Badge, METHOD_COLORS, DEFAULT_METHOD_COLOR }
export type { BadgeColor }
